/**
 * Product utility functions for Gallery Builder
 * Pure utility functions for product filtering and manipulation
 */

/**
 * Filter products by orientation
 * This pattern is repeated 5+ times across the codebase
 * @param {Array} products - Array of product objects
 * @param {string} orientation - 'horizontal' or 'vertical'
 * @returns {Array} Filtered products matching orientation
 */
export function filterByOrientation(products, orientation) {
  if (!products || !Array.isArray(products)) return [];
  if (!orientation) return products;

  return products.filter(product =>
    matchesOrientation(product, orientation)
  );
}

/**
 * Check if a product matches the given orientation
 * @param {Object} product - Product object with tags
 * @param {string} orientation - 'horizontal' or 'vertical'
 * @returns {boolean} True if product matches orientation
 */
export function matchesOrientation(product, orientation) {
  if (!product || !orientation) return false;

  // Check tags for orientation - support both formats for compatibility
  if (product.tags) {
    // Format 1: Shopify actual format (primary) - "Orientation|Horizontal"
    const shopifyTag = `Orientation|${orientation.charAt(0).toUpperCase() + orientation.slice(1)}`;
    // Format 2: Alternative format (for compatibility) - "orientation-horizontal"
    const alternativeTag = `orientation-${orientation}`;

    // Check both formats
    const tagsArray = Array.isArray(product.tags) ? product.tags :
                      (typeof product.tags === 'string' ? [product.tags] : []);

    for (const tag of tagsArray) {
      if (tag === shopifyTag || tag === alternativeTag ||
          (typeof tag === 'string' && (tag.includes(shopifyTag) || tag.includes(alternativeTag)))) {
        return true;
      }
    }
  }

  // Fallback: check image dimensions if available
  if (product.featured_image?.width && product.featured_image?.height) {
    const isHorizontal = product.featured_image.width > product.featured_image.height;
    const matchesByDimension = orientation === 'horizontal' ? isHorizontal : !isHorizontal;

    // Debug log when using dimension fallback
    if (!product._dimensionLogShown) {
      console.log('[Orientation] Using dimension fallback for product:', {
        title: product.title,
        width: product.featured_image.width,
        height: product.featured_image.height,
        isHorizontal,
        requestedOrientation: orientation,
        matches: matchesByDimension
      });
      product._dimensionLogShown = true;
    }

    return matchesByDimension;
  }

  return false;
}

/**
 * Get random products from array (Standard Implementation)
 *
 * USE THIS WHEN:
 * - One-off randomization operations
 * - Initial gallery load
 * - User clicks randomize button
 * - Not in a performance-critical loop
 *
 * DO NOT USE WHEN:
 * - High-frequency operations (use utils/array-utils.js ArrayUtils.randomizeSelection instead)
 * - Inside animation loops
 * - Real-time filtering with continuous updates
 *
 * @param {Array} products - Array of products
 * @param {number} count - Number of products to select
 * @returns {Array} Random selection of products
 */
export function getRandomProducts(products, count) {
  if (!products || !Array.isArray(products) || products.length === 0) {
    return [];
  }

  if (count >= products.length) {
    // Shuffle and return all
    return shuffleArray([...products]);
  }

  const shuffled = shuffleArray([...products]);
  return shuffled.slice(0, count);
}

/**
 * Shuffle an array using Fisher-Yates algorithm (Standard Implementation)
 *
 * USE THIS WHEN:
 * - Shuffling small to medium arrays (< 1000 items)
 * - One-time shuffle operations
 * - Not performance critical
 *
 * NOTE: Creates a new array copy. For in-place shuffling with better
 * performance, use ArrayUtils.shuffleInPlace() from utils/array-utils.js
 *
 * @param {Array} array - Array to shuffle
 * @returns {Array} New shuffled array (original unchanged)
 */
export function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Find product by ID
 * @param {Array} products - Array of products
 * @param {string|number} id - Product ID
 * @returns {Object|null} Product or null if not found
 */
export function findProductById(products, id) {
  if (!products || !id) return null;

  const idString = String(id);
  return products.find(p => String(p.id) === idString) || null;
}

/**
 * Extract product IDs from comma-separated string
 * @param {string} idsString - Comma-separated IDs like "123,456,789"
 * @returns {Array} Array of ID strings
 */
export function parseProductIds(idsString) {
  if (!idsString || typeof idsString !== 'string') return [];

  return idsString
    .split(',')
    .map(id => id.trim())
    .filter(id => id.length > 0);
}

/**
 * Get products by IDs maintaining order
 * @param {Array} products - Array of all products
 * @param {Array} ids - Array of product IDs in desired order
 * @returns {Array} Array of products in same order as IDs
 */
export function getProductsByIds(products, ids) {
  if (!products || !ids || ids.length === 0) return [];

  const productMap = new Map(
    products.map(p => [String(p.id), p])
  );

  return ids
    .map(id => productMap.get(String(id)))
    .filter(Boolean);
}

/**
 * Check if product is available for selection
 * @param {Object} product - Product object
 * @returns {boolean} True if product can be selected
 */
export function isProductAvailable(product) {
  if (!product) return false;

  // Check basic availability
  if (product.available === false) return false;

  // Check if has variants
  if (!product.variants || product.variants.length === 0) return false;

  // Check if at least one variant is available
  const hasAvailableVariant = product.variants.some(v => v.available);
  if (!hasAvailableVariant) return false;

  // Check if has valid image
  if (!product.image && !product.featured_image) return false;

  return true;
}

/**
 * Get default products for orientation
 * @param {Array} products - All products
 * @param {string} orientation - Orientation to filter by
 * @param {number} count - Number of products to return
 * @returns {Array} Default products for the orientation
 */
export function getDefaultProducts(products, orientation, count = 2) {
  const orientationProducts = filterByOrientation(products, orientation);
  const availableProducts = orientationProducts.filter(isProductAvailable);

  if (availableProducts.length >= count) {
    // Return first N available products
    return availableProducts.slice(0, count);
  }

  // Not enough products, return what we have
  return availableProducts;
}